import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/Toast';
import { Button, Input, Select, Card } from '../components/ui';
import PROVINCE from '../utils/province';

// OptionSet Tipologia Edificio
const TIPOLOGIE_EDIFICIO = [
  { value: '100000000', label: 'Edificio storico' },
  { value: '100000001', label: 'Edificio moderno' },
  { value: '100000002', label: 'Villa' },
  { value: '100000003', label: 'Complesso residenziale' },
  { value: '100000004', label: 'Altro' }
];

function NuovoImmobilePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  // Parametri URL
  const editId = searchParams.get('id');
  const rsaIdParam = searchParams.get('rsaId');
  const isEditMode = !!editId;

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [errors, setErrors] = useState({});
  const [rsaList, setRsaList] = useState([]);
  const [loadingRsa, setLoadingRsa] = useState(true);
  const [rsaNome, setRsaNome] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    // Identificazione
    phc_name: '',
    rsaId: rsaIdParam || '',

    // Indirizzo
    phc_indirizzo: '',
    phc_comune: '',
    phc_provincia: '',

    // Dati catastali (array di righe)
    datiCatastali: [
      { superficie: '', foglio: '', particella: '', subalterno: '', categoria: '', classe: '', rendita: '' }
    ],

    // Dati tecnici
    phc_superficiecalpestabile: '',
    phc_numeropiani: '',
    phc_numerostanze: '',
    phc_postilettoautorizzati: '',
    phc_postilettooperativi: '',
    phc_annocostruzione: '',
    phc_annoultimaristrutturazione: '',
    phc_tipologiaedificio: '',

    // Note
    phc_note: ''
  });

  // Carica lista RSA
  useEffect(() => {
    const loadRsaList = async () => {
      setLoadingRsa(true);
      try {
        const result = await apiRequest('/rsa');
        setRsaList((result.value || []).map(r => ({
          value: r.id,
          label: r.nome
        })));

        // Se c'e' rsaIdParam, trova il nome
        if (rsaIdParam) {
          const rsa = (result.value || []).find(r => r.id === rsaIdParam);
          if (rsa) setRsaNome(rsa.nome);
        }
      } catch (error) {
        console.error('Error loading RSA list:', error);
      } finally {
        setLoadingRsa(false);
      }
    };

    loadRsaList();
  }, [apiRequest, rsaIdParam]);

  // Carica dati immobile in modalita' modifica
  useEffect(() => {
    if (!isEditMode) return;

    const loadImmobile = async () => {
      setLoadingData(true);
      try {
        const data = await apiRequest(`/immobili/${editId}`);

        setFormData({
          phc_name: data.nome || '',
          rsaId: data.rsaId || '',
          phc_indirizzo: data.indirizzo || '',
          phc_comune: data.comune || '',
          phc_provincia: data.provincia || '',
          datiCatastali: data.datiCatastali && data.datiCatastali.length > 0
            ? data.datiCatastali.map(d => ({
                superficie: d.superficie ?? '',
                foglio: d.foglio || '',
                particella: d.particella || '',
                subalterno: d.subalterno || '',
                categoria: d.categoria || '',
                classe: d.classe || '',
                rendita: d.rendita ?? ''
              }))
            : [{
                superficie: data.superficieCatastale || '',
                foglio: data.foglio || '',
                particella: data.particella || '',
                subalterno: data.subalterno || '',
                categoria: data.categoriaCatastale || '',
                classe: data.classeCatastale || '',
                rendita: data.renditaCatastale || ''
              }],
          phc_superficiecalpestabile: data.superficieCalpestabile || '',
          phc_numeropiani: data.numeroPiani || '',
          phc_numerostanze: data.numeroStanze || '',
          phc_postilettoautorizzati: data.postiLettoAutorizzati || '',
          phc_postilettooperativi: data.postiLettoOperativi || '',
          phc_annocostruzione: data.annoCostruzione || '',
          phc_annoultimaristrutturazione: data.annoUltimaRistrutturazione || '',
          phc_tipologiaedificio: data.tipologiaEdificio !== undefined ? String(data.tipologiaEdificio) : '',
          phc_note: data.note || ''
        });

        if (data.rsaNome) setRsaNome(data.rsaNome);
      } catch (error) {
        console.error('Error loading immobile:', error);
        showToast('Errore nel caricamento dell\'immobile', 'error');
        navigate('/rsa');
      } finally {
        setLoadingData(false);
      }
    };

    loadImmobile();
  }, [editId, isEditMode, apiRequest, navigate, showToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Gestione righe catastali
  const addCatastaleRow = () => {
    setFormData(prev => ({
      ...prev,
      datiCatastali: [
        ...prev.datiCatastali,
        { superficie: '', foglio: '', particella: '', subalterno: '', categoria: '', classe: '', rendita: '' }
      ]
    }));
  };

  const removeCatastaleRow = (index) => {
    setFormData(prev => ({
      ...prev,
      datiCatastali: prev.datiCatastali.filter((_, i) => i !== index)
    }));
  };

  const handleCatastaleChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      datiCatastali: prev.datiCatastali.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      )
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.phc_name.trim()) {
      newErrors.phc_name = 'Il nome/descrizione è obbligatorio';
    }

    if (!formData.rsaId) {
      newErrors.rsaId = 'La RSA è obbligatoria';
    }

    // Validazione anno costruzione
    if (formData.phc_annocostruzione) {
      const anno = parseInt(formData.phc_annocostruzione);
      if (isNaN(anno) || anno < 1800 || anno > new Date().getFullYear()) {
        newErrors.phc_annocostruzione = 'Anno non valido';
      }
    }

    // Validazione numeri
    const numericFields = [
      'phc_superficiecalpestabile',
      'phc_numeropiani', 'phc_numerostanze', 'phc_postilettoautorizzati', 'phc_postilettooperativi'
    ];
    numericFields.forEach(field => {
      if (formData[field] && isNaN(parseFloat(formData[field]))) {
        newErrors[field] = 'Deve essere un numero';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showToast('Correggi gli errori nel form', 'error');
      return;
    }

    setLoading(true);

    try {
      // Prepara dati catastali: filtra righe vuote, converti numeri
      const datiCatastali = formData.datiCatastali
        .filter(row => row.foglio || row.particella || row.subalterno || row.categoria || row.rendita || row.superficie)
        .map(row => ({
          superficie: row.superficie ? parseFloat(row.superficie) : null,
          foglio: row.foglio || '',
          particella: row.particella || '',
          subalterno: row.subalterno || '',
          categoria: row.categoria || '',
          classe: row.classe || '',
          rendita: row.rendita ? parseFloat(row.rendita) : null
        }));

      const dataToSend = {
        ...formData,
        // Dati catastali multipli
        datiCatastali,
        // Converti numeri tecnici
        phc_superficiecalpestabile: formData.phc_superficiecalpestabile ? parseFloat(formData.phc_superficiecalpestabile) : null,
        phc_numeropiani: formData.phc_numeropiani ? parseInt(formData.phc_numeropiani) : null,
        phc_numerostanze: formData.phc_numerostanze ? parseInt(formData.phc_numerostanze) : null,
        phc_postilettoautorizzati: formData.phc_postilettoautorizzati ? parseInt(formData.phc_postilettoautorizzati) : null,
        phc_postilettooperativi: formData.phc_postilettooperativi ? parseInt(formData.phc_postilettooperativi) : null,
        phc_annocostruzione: formData.phc_annocostruzione ? parseInt(formData.phc_annocostruzione) : null,
        phc_annoultimaristrutturazione: formData.phc_annoultimaristrutturazione ? parseInt(formData.phc_annoultimaristrutturazione) : null,
        phc_tipologiaedificio: formData.phc_tipologiaedificio ? parseInt(formData.phc_tipologiaedificio) : null
      };

      // Rimuovi campi vuoti
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === '' || dataToSend[key] === null) {
          delete dataToSend[key];
        }
      });

      if (isEditMode) {
        await apiRequest(`/immobili/${editId}`, {
          method: 'PUT',
          body: JSON.stringify(dataToSend)
        });
        showToast('Immobile aggiornato con successo!', 'success');
      } else {
        await apiRequest('/immobili', {
          method: 'POST',
          body: JSON.stringify(dataToSend)
        });
        showToast('Immobile creato con successo!', 'success');
      }

      // Torna alla RSA
      if (formData.rsaId) {
        navigate(`/rsa/${formData.rsaId}`);
      } else {
        navigate('/rsa');
      }
    } catch (error) {
      console.error('Error saving immobile:', error);
      showToast(error.message || 'Errore durante il salvataggio', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <a href="/rsa" className="hover:text-primary-600">RSA</a>
          {rsaNome && (
            <>
              <span>/</span>
              <a href={`/rsa/${formData.rsaId || rsaIdParam}`} className="hover:text-primary-600">{rsaNome}</a>
            </>
          )}
          <span>/</span>
          <span>{isEditMode ? 'Modifica Immobile' : 'Nuovo Immobile'}</span>
        </div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? 'Modifica Immobile' : 'Nuovo Immobile / Dato Catastale'}
        </h1>
        <p className="text-gray-500 mt-1">
          {isEditMode
            ? 'Modifica i dati catastali e tecnici dell\'immobile'
            : 'Inserisci i dati catastali e tecnici di un nuovo immobile o mappale'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Sezione: Identificazione */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>🏠</span> Identificazione
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Nome / Descrizione *"
                name="phc_name"
                value={formData.phc_name}
                onChange={handleChange}
                placeholder="es. Edificio principale, Dependance, Ala Nord"
                error={errors.phc_name}
                maxLength={200}
              />
            </div>

            <div className="md:col-span-2">
              <Select
                label="RSA di appartenenza *"
                name="rsaId"
                value={formData.rsaId}
                onChange={handleChange}
                options={rsaList}
                placeholder={loadingRsa ? 'Caricamento...' : 'Seleziona RSA...'}
                error={errors.rsaId}
                disabled={!!rsaIdParam}
              />
            </div>
          </div>
        </Card>

        {/* Sezione: Indirizzo */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📍</span> Indirizzo Immobile
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Indirizzo"
                name="phc_indirizzo"
                value={formData.phc_indirizzo}
                onChange={handleChange}
                placeholder="Via/Piazza, numero civico"
                helperText="Se diverso dall'indirizzo principale della RSA"
                maxLength={200}
              />
            </div>

            <Input
              label="Comune"
              name="phc_comune"
              value={formData.phc_comune}
              onChange={handleChange}
              placeholder="es. Padova"
              maxLength={100}
            />

            <Select
              label="Provincia"
              name="phc_provincia"
              value={formData.phc_provincia}
              onChange={handleChange}
              options={PROVINCE}
              placeholder="Seleziona..."
            />
          </div>
        </Card>

        {/* Sezione: Dati Catastali (tabella dinamica) */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span>📋</span> Dati Catastali
            </h2>
            <button
              type="button"
              onClick={addCatastaleRow}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
            >
              + Aggiungi riga
            </button>
          </div>

          <div className="mb-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
            <p className="font-medium mb-1">Dove trovare questi dati?</p>
            <p className="text-blue-700">
              I dati catastali si trovano nella visura catastale dell'immobile.
              Ogni riga corrisponde a un mappale/subalterno della visura.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="px-2 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Mq</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Foglio</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Mappale</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Sub.</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Cat.</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Classe</th>
                  <th className="px-2 py-2 text-left font-medium text-gray-600 whitespace-nowrap">Rendita (EUR)</th>
                  <th className="px-2 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {formData.datiCatastali.map((row, index) => (
                  <tr key={index} className="border-b last:border-b-0">
                    <td className="px-1 py-1">
                      <input
                        type="number"
                        step="0.01"
                        value={row.superficie}
                        onChange={(e) => handleCatastaleChange(index, 'superficie', e.target.value)}
                        className="w-20 border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="-"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input
                        type="text"
                        value={row.foglio}
                        onChange={(e) => handleCatastaleChange(index, 'foglio', e.target.value)}
                        className="w-16 border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="-"
                        maxLength={20}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input
                        type="text"
                        value={row.particella}
                        onChange={(e) => handleCatastaleChange(index, 'particella', e.target.value)}
                        className="w-16 border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="-"
                        maxLength={20}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input
                        type="text"
                        value={row.subalterno}
                        onChange={(e) => handleCatastaleChange(index, 'subalterno', e.target.value)}
                        className="w-14 border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="-"
                        maxLength={20}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input
                        type="text"
                        value={row.categoria}
                        onChange={(e) => handleCatastaleChange(index, 'categoria', e.target.value)}
                        className="w-20 border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="es. D/4"
                        maxLength={20}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input
                        type="text"
                        value={row.classe}
                        onChange={(e) => handleCatastaleChange(index, 'classe', e.target.value)}
                        className="w-14 border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="-"
                        maxLength={10}
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input
                        type="number"
                        step="0.01"
                        value={row.rendita}
                        onChange={(e) => handleCatastaleChange(index, 'rendita', e.target.value)}
                        className="w-28 border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                        placeholder="0,00"
                      />
                    </td>
                    <td className="px-1 py-1 text-center">
                      {formData.datiCatastali.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCatastaleRow(index)}
                          className="text-red-400 hover:text-red-600 p-1 rounded hover:bg-red-50 transition-colors"
                          title="Rimuovi riga"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              {formData.datiCatastali.length > 1 && (
                <tfoot>
                  <tr className="border-t bg-gray-50 font-medium text-sm">
                    <td className="px-2 py-2 text-gray-700">
                      {formData.datiCatastali.reduce((s, r) => s + (parseFloat(r.superficie) || 0), 0).toLocaleString('it-IT')} mq
                    </td>
                    <td colSpan={5} className="px-2 py-2 text-right text-gray-500">
                      {formData.datiCatastali.length} voci | Rendita totale:
                    </td>
                    <td className="px-2 py-2 text-gray-700">
                      EUR {formData.datiCatastali.reduce((s, r) => s + (parseFloat(r.rendita) || 0), 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>

        {/* Sezione: Dati Tecnici */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>🔧</span> Dati Tecnici
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Superficie Calpestabile (mq)"
              name="phc_superficiecalpestabile"
              type="number"
              step="0.01"
              value={formData.phc_superficiecalpestabile}
              onChange={handleChange}
              placeholder="es. 1200"
              error={errors.phc_superficiecalpestabile}
            />

            <Input
              label="Numero Piani"
              name="phc_numeropiani"
              type="number"
              value={formData.phc_numeropiani}
              onChange={handleChange}
              placeholder="es. 3"
              error={errors.phc_numeropiani}
            />

            <Input
              label="Numero Stanze"
              name="phc_numerostanze"
              type="number"
              value={formData.phc_numerostanze}
              onChange={handleChange}
              placeholder="es. 50"
              error={errors.phc_numerostanze}
            />

            <Input
              label="Posti Letto Autorizzati"
              name="phc_postilettoautorizzati"
              type="number"
              value={formData.phc_postilettoautorizzati}
              onChange={handleChange}
              placeholder="es. 80"
              helperText="Posti letto da autorizzazione"
              error={errors.phc_postilettoautorizzati}
            />

            <Input
              label="Posti Letto Operativi"
              name="phc_postilettooperativi"
              type="number"
              value={formData.phc_postilettooperativi}
              onChange={handleChange}
              placeholder="es. 75"
              helperText="Posti letto attualmente attivi"
              error={errors.phc_postilettooperativi}
            />

            <Input
              label="Anno Costruzione"
              name="phc_annocostruzione"
              type="number"
              value={formData.phc_annocostruzione}
              onChange={handleChange}
              placeholder="es. 1985"
              error={errors.phc_annocostruzione}
            />

            <Input
              label="Anno Ultima Ristrutturazione"
              name="phc_annoultimaristrutturazione"
              type="number"
              value={formData.phc_annoultimaristrutturazione}
              onChange={handleChange}
              placeholder="es. 2020"
            />

            <Select
              label="Tipologia Edificio"
              name="phc_tipologiaedificio"
              value={formData.phc_tipologiaedificio}
              onChange={handleChange}
              options={TIPOLOGIE_EDIFICIO}
              placeholder="Seleziona..."
            />
          </div>
        </Card>

        {/* Sezione: Note */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📝</span> Note
          </h2>

          <textarea
            name="phc_note"
            value={formData.phc_note}
            onChange={handleChange}
            placeholder="Note aggiuntive sull'immobile (vincoli, caratteristiche particolari, ecc.)..."
            className="form-input w-full h-32"
            maxLength={10000}
          />
        </Card>

        {/* Form Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              if (formData.rsaId || rsaIdParam) {
                navigate(`/rsa/${formData.rsaId || rsaIdParam}`);
              } else {
                navigate('/rsa');
              }
            }}
          >
            Annulla
          </Button>

          <Button
            type="submit"
            variant="primary"
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {isEditMode ? 'Salvataggio...' : 'Creazione...'}
              </span>
            ) : (
              isEditMode ? 'Salva Modifiche' : 'Crea Immobile'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default NuovoImmobilePage;
